import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ApiError,
  api,
  ESTADOS_CASO,
  jimiRevisar,
  queclinkRevisar,
  queclinkVincular,
  quitarDuplicado,
  type CasoDetalle as CasoT,
  type EstadoCaso,
  type UserMini,
} from "@/lib/api";
import { useSwipeCasos } from "@/lib/useSwipeCasos";
import { navegarComoCarrusel, type DirCaso } from "@/lib/transicionCaso";
import { ESTADO_CASO_PILL } from "@/lib/estadoCaso";
import { useAuth, canEdit } from "@/lib/auth";
import { leerRecorrido } from "@/lib/recorridoCasos";
import {
  IconoCorreos,
  IconoEvidencias,
  IconoImeis,
  IconoNotas,
  IconoRecordatorios,
  IconoAgregar,
  IconoCerrar,
  IconoDuplicado,
  IconoNotificar,
  IconoResponder,
  IconoRevisar,
  IconoTicket,
} from "@/ui/Iconos";
import CasoContactosPanel from "@/components/CasoContactosPanel";
import ProveedorVistoModal from "@/components/casos/ProveedorVistoModal";
import CasoEquiposResumen from "@/components/CasoEquiposResumen";
import CasoClasificacionPanel from "@/components/CasoClasificacionPanel";
import AgregarImeiModal from "@/components/AgregarImeiModal";
import NuevoCasoModal, { type NuevoCasoPrefill } from "@/components/NuevoCasoModal";
import PanelHilo from "@/components/PanelHilo";
import PanelHitos from "@/components/PanelHitos";
import HandoffModal from "@/components/HandoffModal";
import EditarModelosModal from "@/components/EditarModelosModal";
import TabCorreos from "@/components/TabCorreos";
import TabEvidencias from "@/components/TabEvidencias";
import TabImeis from "@/components/TabImeis";
import TabNotas from "@/components/TabNotas";
import TabRecordatorios from "@/components/TabRecordatorios";
import CerrarCasoModal from "@/components/CerrarCasoModal";
import MarcarDuplicadoModal from "@/components/MarcarDuplicadoModal";
import AbrirTicketJimiModal from "@/components/AbrirTicketJimiModal";
import AsignarAsesorModal from "@/components/asesor/AsignarAsesorModal";
import ResponderProveedorModal from "@/components/ResponderProveedorModal";
import CrearTicketQueclinkModal from "@/components/CrearTicketQueclinkModal";
import AyudaVista from "@/ui/AyudaVista";
import { ayudaCasoDetalle } from "@/components/ayuda/vistas/casoDetalle";
import CodigoCopiable from "@/ui/CodigoCopiable";
import { useToast } from "@/ui/Toast";


const TABS_VALIDOS = ["notas", "imeis", "evidencias", "correos", "recordatorios"] as const;
type Tab = (typeof TABS_VALIDOS)[number];

const TAB_LABEL: Record<Tab, string> = {
  notas: "Bitácora interna",
  imeis: "IMEIs",
  evidencias: "Evidencias",
  correos: "Correos",
  recordatorios: "Recordatorios",
};

// Etiquetas de la barra del celular. Van COMPLETAS: "Evid." y "Record." no son
// palabras y obligaban a adivinar. Caben en 9 px, que es el tamaño de la barra.
const TAB_CORTO: Record<Tab, string> = {
  notas: "Notas",
  imeis: "IMEIs",
  evidencias: "Evidencias",
  correos: "Correos",
  recordatorios: "Recordatorios",
};

const TAB_ICON: Record<Tab, (p: { className?: string }) => JSX.Element> = {
  notas: IconoNotas,
  imeis: IconoImeis,
  evidencias: IconoEvidencias,
  correos: IconoCorreos,
  recordatorios: IconoRecordatorios,
};

/**
 * Casos ya traídos, por folio.
 *
 * Existe para que pasar de caso jalando con el dedo NO espere a la red: al
 * asentarse un caso se precargan sus vecinos del recorrido, así que el gesto
 * casi siempre encuentra el siguiente ya listo y la animación corre sola. Sin
 * esto la pantalla se quedaba invisible lo que tardara el celular en contestar
 * y se sentía como si se trabara.
 *
 * Vive fuera del componente (module scope) a propósito: sobrevive a los cambios
 * de folio, que son la única navegación que nos importa acelerar. Se limpia con
 * un tope simple porque un caso viejo en memoria no estorba, pero mil sí.
 */
const CACHE_CASOS = new Map<string, CasoT>();
const CACHE_TOPE = 12;

function guardarEnCache(folio: string, caso: CasoT) {
  CACHE_CASOS.delete(folio);
  CACHE_CASOS.set(folio, caso);
  if (CACHE_CASOS.size > CACHE_TOPE) {
    const masViejo = CACHE_CASOS.keys().next().value;
    if (masViejo) CACHE_CASOS.delete(masViejo);
  }
}

/** Trae un caso por folio en UNA petición. `null` si no existe. */
async function traerCaso(folio: string): Promise<CasoT | null> {
  try {
    return await api.get<CasoT>(`/api/casos/por-folio/${encodeURIComponent(folio)}`);
  } catch {
    return null;
  }
}

export default function CasoDetalle() {
  const { folio } = useParams<{ folio: string }>();
  const me = useAuth((s) => s.user);
  const toast = useToast();
  const nav = useNavigate();

  const [caso, setCaso] = useState<CasoT | null>(null);
  const [proveedorVistoOpen, setProveedorVistoOpen] = useState(false);
  // `cargando` ya no decide si se pinta la página (ver el guard de abajo): se
  // usa para atenuarla un poco mientras llega el caso nuevo, sin desmontarla.
  const [cargando, setLoading] = useState(true);
  // Entre soltar el dedo/apretar la flecha y ver el movimiento, si el vecino
  // no alcanzó a precargarse, `irACaso` espera la red. Antes esa espera no
  // se veía (medido: la precarga la evita casi siempre, ~50 ms), así que la
  // única vez que tocaba la red parecía que el gesto no había hecho nada.
  const [esperandoVecino, setEsperandoVecino] = useState(false);
  // Al cambiar de folio, si el caso ya venía precargado se adopta EN ESTE MISMO
  // render, no en un efecto. Es lo que permite que el carrusel tome la foto del
  // «después» con el caso nuevo ya pintado: con un efecto llegaría un cuadro
  // tarde y la transición acabaría animando el caso viejo contra sí mismo.
  const [folioVisto, setFolioVisto] = useState(folio);
  if (folio !== folioVisto) {
    setFolioVisto(folio);
    const yaEsta = folio ? CACHE_CASOS.get(folio) : undefined;
    // Sin caché se conserva a la vista el caso anterior y `load()` lo releva.
    if (yaEsta) setCaso(yaEsta);
  }
  // El tab vive en la URL (?tab=) para que recargar lo conserve y el link
  // sea compartible. Default "notas" si falta o es inválido.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab: Tab = (TABS_VALIDOS as readonly string[]).includes(tabParam ?? "")
    ? (tabParam as Tab)
    : "notas";
  function setTab(t: Tab) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", t);
        return next;
      },
      { replace: true },
    );
  }
  // Correo a abrir/resaltar al saltar desde el evento "correo enviado" de la bitácora.
  const [correoAAbrir, setCorreoAAbrir] = useState<number | null>(null);
  const [confirmCerrar, setConfirmCerrar] = useState(false);
  const [asignables, setAsignables] = useState<UserMini[]>([]);
  const [nuevoRelacionadoOpen, setNuevoRelacionadoOpen] = useState(false);
  const [agregarImeiOpen, setAgregarImeiOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [editarModelosOpen, setEditarModelosOpen] = useState(false);
  const [duplicadoOpen, setDuplicadoOpen] = useState(false);
  const [asesorOpen, setAsesorOpen] = useState(false);
  const [revisandoJimi, setRevisandoJimi] = useState(false);
  const [responderJimiOpen, setResponderJimiOpen] = useState(false);
  const [revisandoQC, setRevisandoQC] = useState(false);
  const [responderQCOpen, setResponderQCOpen] = useState(false);
  const [crearQCOpen, setCrearQCOpen] = useState(false);
  const [jimiModal, setJimiModal] = useState<
    null | { modo: "crear" | "manual"; numero?: string; sinEquipos?: boolean }
  >(null);
  // Bumpear esta key tras crear un caso relacionado fuerza al PanelHilo a re-fetch
  const [hiloRefreshKey, setHiloRefreshKey] = useState(0);

  async function load(opts: { silent?: boolean } = {}) {
    if (!folio) return;
    // Modo silent: no toca el flag de loading global del page, asi el
    // subtree no se desmonta durante el re-fetch. Lo usan los sub-tabs
    // (TabNotas, TabCorreos, etc.) que disparan `onChanged` y necesitan
    // que el DOM permanezca estable (refs, focus, scroll position).
    // Si ya lo traíamos precargado, se pinta de inmediato: es lo que hace que
    // el gesto de pasar de caso no espere a la red. Se refresca detrás.
    const enCache = CACHE_CASOS.get(folio);
    if (enCache && !opts.silent) {
      setCaso(enCache);
      setLoading(false);
      void traerCaso(folio).then((fresco) => {
        if (fresco) {
          guardarEnCache(folio, fresco);
          setCaso((actual) => (actual?.folio === folio ? fresco : actual));
        }
      });
      return;
    }

    if (!opts.silent) setLoading(true);
    try {
      const detalle = await api.get<CasoT>(
        `/api/casos/por-folio/${encodeURIComponent(folio)}`,
      );
      guardarEnCache(folio, detalle);
      setCaso(detalle);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        toast.error("Caso no encontrado", folio);
        nav("/casos");
        return;
      }
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar el caso", msg);
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }

  async function revisarJimi() {
    if (!caso || revisandoJimi) return;
    setRevisandoJimi(true);
    try {
      const r = await jimiRevisar(caso.id);
      if (r.nuevas > 0) {
        toast.success(
          r.nuevas === 1 ? "Jimi contestó" : `Jimi contestó ${r.nuevas} veces`,
          "Lo puse en la bitácora del caso",
        );
        await load({ silent: true });
      } else {
        toast.success("Sin novedades", `Jimi no ha contestado en ${r.work_order_no}`);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude revisar el ticket", msg);
    } finally {
      setRevisandoJimi(false);
    }
  }

  async function revisarQueclink() {
    if (!caso || revisandoQC) return;
    setRevisandoQC(true);
    try {
      const r = await queclinkRevisar(caso.id);
      if (r.nuevas > 0) {
        toast.success(
          r.nuevas === 1 ? "Queclink contestó" : `Queclink contestó ${r.nuevas} veces`,
          "Lo puse en la bitácora del caso",
        );
        await load({ silent: true });
      } else {
        toast.success("Sin novedades", `Queclink no ha contestado en el ticket #${r.ticket_id}`);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude revisar el ticket", msg);
    } finally {
      setRevisandoQC(false);
    }
  }

  async function vincularQueclink() {
    if (!caso || revisandoQC) return;
    setRevisandoQC(true);
    try {
      const r = await queclinkVincular(caso.id);
      if (r.ticket_id) {
        toast.success("Ticket encontrado", `#${r.ticket_id} quedó ligado a este caso`);
        await load({ silent: true });
      } else {
        toast.error(
          "No encontré el ticket",
          "Ninguno en Queclink trae el TE de este caso en el asunto",
        );
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude buscar el ticket", msg);
    } finally {
      setRevisandoQC(false);
    }
  }

  // Wrapper para pasar a los sub-componentes que disparen onChanged.
  // No usar `load` directo porque eso re-monta el subtree (spinner).
  const refreshSilencioso = () => load({ silent: true });

  useEffect(() => {
    void load();
    api.get<UserMini[]>("/api/users/asignables").then(setAsignables).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folio]);

  async function actualizarEstado(nuevo: EstadoCaso) {
    if (!caso) return;
    try {
      const updated = await api.patch<CasoT>(`/api/casos/${caso.id}`, { estado: nuevo });
      setCaso(updated);
      toast.success("Estado actualizado", nuevo);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo actualizar", msg);
    }
  }

  async function actualizarAsignacion(uid: number | "") {
    if (!caso) return;
    try {
      const updated = await api.patch<CasoT>(`/api/casos/${caso.id}`, {
        asignado_a_id: uid === "" ? null : uid,
      });
      setCaso(updated);
      toast.success("Asignación actualizada");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo asignar", msg);
    }
  }

  async function marcarRetornoHoy() {
    if (!caso) return;
    try {
      const updated = await api.patch<CasoT>(`/api/casos/${caso.id}`, {
        fecha_retorno_proveedor: todayISO(),
      });
      setCaso(updated);
      toast.success("Retorno del proveedor registrado");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo registrar", msg);
    }
  }

  async function quitarMarcaDuplicado() {
    if (!caso) return;
    try {
      const updated = await quitarDuplicado(caso.id);
      setCaso(updated);
      toast.success("Marca de duplicado quitada");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No se pudo quitar la marca", msg);
    }
  }

  // Prefill para "+ Caso relacionado": cliente, marca, modelo y los IMEIs
  // del caso actual. El usuario los puede ajustar antes de crear.
  const relacionadoPrefill: NuevoCasoPrefill | undefined = useMemo(() => {
    if (!caso) return undefined;
    return {
      cliente_id: caso.cliente_id,
      marca: caso.marca ?? "",
      modelo: caso.modelo ?? "",
      imeis_raw: caso.imeis.map((i) => i.imei).join(", "),
      caso_padre_id: caso.id,
      caso_padre_folio: caso.folio,
    };
  }, [caso]);

  // Navegación «Anterior / Siguiente» sobre el recorrido de la lista
  // (respeta tus filtros y orden). Sin recorrido (link directo) no hay flechas.
  const recorrido = useMemo(() => leerRecorrido(), []);
  const idxRecorrido = folio ? recorrido.indexOf(folio) : -1;
  const folioPrev = idxRecorrido > 0 ? recorrido[idxRecorrido - 1] : null;
  const folioNext =
    idxRecorrido >= 0 && idxRecorrido < recorrido.length - 1
      ? recorrido[idxRecorrido + 1]
      : null;

  /**
   * La ÚNICA puerta para pasar de caso: la usan el dedo, las flechas de la barra
   * y Ctrl+←/→.
   *
   * Antes cada camino llamaba `nav()` por su cuenta y solo el dedo animaba algo:
   * con el botón el caso cambiaba en seco (medido: 0 px de movimiento en 96
   * cuadros) y encima te dejaba a media página del caso anterior.
   */
  async function irACaso(destino: string | null, dir: DirCaso, desde = 0) {
    if (!destino) return;
    // El carrusel necesita el caso nuevo YA en mano, porque la foto del
    // «después» se toma en cuanto React pinta. Con la precarga de vecinos esto
    // casi nunca toca la red; si todavía no llegó, se espera aquí.
    if (!CACHE_CASOS.has(destino)) {
      setEsperandoVecino(true);
      try {
        const traido = await traerCaso(destino);
        if (traido) guardarEnCache(destino, traido);
      } finally {
        setEsperandoVecino(false);
      }
    }
    navegarComoCarrusel({
      dir,
      desde,
      aplicar: () => {
        nav(`/casos/${destino}`);
        // Empezar arriba: quedarse a media página del caso anterior desorienta.
        window.scrollTo({ top: 0 });
      },
    });
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Ctrl+←/→ a propósito: las flechas SOLAS son del carrusel del visor
      // de evidencias (y del scroll); robarlas a nivel página cambiaba de
      // caso a media revisión de fotos.
      if (!e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const t = e.target as HTMLElement;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable) return;
      // Modal o visor de evidencias abiertos = candado (no cambiar de caso).
      if (document.querySelector('[role="dialog"], [data-visor-archivo]')) return;
      if (e.key === "ArrowLeft" && folioPrev) {
        e.preventDefault();
        void irACaso(folioPrev, "prev");
      }
      if (e.key === "ArrowRight" && folioNext) {
        e.preventDefault();
        void irACaso(folioNext, "next");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [folioPrev, folioNext, nav]);

  // Precarga de los vecinos del recorrido. Se dispara cuando el caso actual ya
  // se pintó, así no compite con su propia petición.
  useEffect(() => {
    if (!caso) return;
    const id = window.setTimeout(() => {
      for (const f of [folioPrev, folioNext]) {
        if (!f || CACHE_CASOS.has(f)) continue;
        void traerCaso(f).then((c) => c && guardarEnCache(f, c));
      }
    }, 250);
    return () => window.clearTimeout(id);
  }, [caso, folioPrev, folioNext]);

  // Mismo recorrido que las flechas y que Ctrl+←/→: jalar con el dedo es la
  // tercera puerta al mismo lado, no un mecanismo aparte.
  const swipe = useSwipeCasos({
    hayPrev: folioPrev !== null,
    hayNext: folioNext !== null,
    irPrev: (desde) => void irACaso(folioPrev, "prev", desde),
    irNext: (desde) => void irACaso(folioNext, "next", desde),
    activo: idxRecorrido >= 0 && recorrido.length > 1,
  });

  // Solo se enseña "Cargando…" cuando NO hay nada que enseñar (primera
  // entrada). Al cambiar de caso, `caso` conserva el anterior hasta que llega
  // el nuevo: con `loading || !caso` la página se quedaba en blanco un
  // instante y ESE era el escalón que se sentía al pasar de caso, no la
  // animación. El contenido viejo ya va invisible por la salida del gesto.
  if (!caso) {
    return <p className="text-sm text-zinc-500">Cargando...</p>;
  }

  return (
    <>
      {/* Pista de a dónde vas mientras jalas. Aparece solo cuando ya pasaste el
          punto de no retorno, así se aprende dónde está el límite.

          ⚠️ Va FUERA del contenedor de abajo, y por dos razones medidas:
          1. `space-y-4` es `> * ~ * { margin-top: 1rem }`. Como hijo, al
             aparecer empujaba al primer bloque real y TODA la ventana bajaba
             16 px de golpe (y subía al desaparecer). Ese era «el escalón».
          2. Un `position: fixed` dentro de un elemento con `transform` se ancla
             a ESE elemento, no al viewport. Adentro se colgaba del contenedor de
             ~4000 px de alto y encima viajaba con el arrastre. */}
      {swipe.destino && (
        <div
          className={`md:hidden pointer-events-none fixed top-1/2 z-40 -translate-y-1/2 rounded-full bg-accent-500/90 px-3 py-2 text-xs font-medium text-ink-950 shadow-lg ${
            swipe.destino === "prev" ? "left-2" : "right-2"
          }`}
        >
          {swipe.destino === "prev" ? "‹ Anterior" : "Siguiente ›"}
        </div>
      )}
    <div
      className="space-y-4"
      {...swipe.handlers}
      style={{
        ...swipe.estilo,
        // Mientras llega el caso nuevo se atenúa; NO se desmonta. Es un
        // estado fijo, no una animación: animar la opacidad de este subárbol
        // (~4000 px de alto) es justo lo que hacía que se sintiera trabado.
        // La misma atenuación cubre la espera del vecino en `irACaso`: es el
        // mismo «ya viene el caso», solo que antes de navegar.
        ...((cargando || esperandoVecino) && !swipe.arrastrando ? { opacity: 0.55 } : null),
        ...(esperandoVecino ? { cursor: "progress" } : null),
      }}
      aria-busy={cargando || esperandoVecino}
    >
      <div className="flex items-center justify-between gap-2 text-sm">
        <Link to="/casos" className="text-zinc-400 hover:text-white">&larr; Casos</Link>
        {idxRecorrido >= 0 && recorrido.length > 1 && (
          <div className="flex items-center gap-1.5 text-xs">
            <button
              type="button"
              disabled={!folioPrev}
              onClick={() => void irACaso(folioPrev, "prev")}
              className="px-2 py-1 rounded border border-ink-700 text-zinc-300 hover:border-accent-500/50 hover:text-accent-300 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              ‹ Anterior
            </button>
            <span className="text-zinc-500 px-1 whitespace-nowrap">
              {idxRecorrido + 1} de {recorrido.length}
            </span>
            <button
              type="button"
              disabled={!folioNext}
              onClick={() => void irACaso(folioNext, "next")}
              className="px-2 py-1 rounded border border-ink-700 text-zinc-300 hover:border-accent-500/50 hover:text-accent-300 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              Siguiente ›
            </button>
          </div>
        )}
      </div>

      <div className="card space-y-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          {/* Columna izquierda: identidad del caso (folio/cliente/modelo)
              + contactos del caso debajo del modelo, separados con una
              linea corta. Quedan en la misma columna para dar
              continuidad visual con el nombre del cliente arriba. */}
          <div className="flex-1 min-w-0 space-y-2">
            <div>
              <h1 className="font-mono text-accent-400 text-base sm:text-xl">
                <CodigoCopiable valor={caso.folio} etiqueta="folio" />
              </h1>
              <p className="text-sm text-zinc-300 mt-1">
                <Link to="/casos" className="hover:underline">{caso.cliente_nombre}</Link>
                {caso.ticket_externo && (
                  <span className="text-zinc-500">
                    {" · "}
                    <CodigoCopiable
                      valor={caso.ticket_externo}
                      etiqueta="TE"
                      className="font-mono hover:text-zinc-300"
                    />
                  </span>
                )}
                {/* Ticket de Queclink: el número no se veía por ningún lado
                    aunque el caso ya estuviera ligado. Copiable, como el de Jimi. */}
                {caso.queclink_ticket_id && (
                  <span
                    className="ml-1.5 inline-flex items-center gap-1 rounded border border-sky-500/30 bg-sky-500/10 px-1.5 py-0.5 text-[11px] text-sky-300 align-middle"
                  >
                    🎫 Queclink{" "}
                    <CodigoCopiable
                      valor={String(caso.queclink_ticket_id)}
                      etiqueta="ticket de Queclink"
                      className="font-mono hover:text-sky-100"
                    />
                    {caso.queclink_ticket_estado && (
                      <span className="text-sky-200/80">· {caso.queclink_ticket_estado}</span>
                    )}
                  </span>
                )}
                {caso.jimi_work_order_no && (
                  <span
                    className="ml-1.5 inline-flex items-center gap-1 rounded border border-teal-500/30 bg-teal-500/10 px-1.5 py-0.5 text-[11px] text-teal-300 align-middle"
                  >
                    🎫 Jimi{" "}
                    <CodigoCopiable
                      valor={caso.jimi_work_order_no}
                      etiqueta="ticket de Jimi"
                      className="font-mono hover:text-teal-100"
                    />
                    {caso.jimi_ticket_estado && (
                      <span className="text-teal-200/80">· {caso.jimi_ticket_estado}</span>
                    )}
                    {caso.jimi_origen === "manual" && canEdit(me) && (
                      <button
                        type="button"
                        onClick={() =>
                          setJimiModal({ modo: "manual", numero: caso.jimi_work_order_no ?? "" })
                        }
                        className="ml-1 text-teal-400 hover:text-teal-200"
                        aria-label="Editar el número de Jimi"
                      >
                        ✏️
                      </button>
                    )}
                  </span>
                )}
              </p>
              {/* Modelos del caso: lista si hay varios, fallback al campo
                  simple (caso.marca/modelo) para casos anteriores al feature. */}
              {caso.modelos.length > 1 ? (
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {caso.modelos.map((m, i) => (
                    <span
                      key={m.id ?? i}
                      className="inline-flex items-center gap-1 rounded-md border border-ink-600 bg-ink-800 px-2 py-0.5 text-xs text-zinc-300"
                    >
                      {i === 0 && (
                        <span className="text-[9px] text-accent-400 font-medium uppercase tracking-wide">Principal&nbsp;·&nbsp;</span>
                      )}
                      {m.marca && <span className="text-zinc-400">{m.marca}</span>}
                      <span>{m.modelo}</span>
                    </span>
                  ))}
                  {canEdit(me) && (
                    <button
                      type="button"
                      onClick={() => setEditarModelosOpen(true)}
                      className="text-zinc-500 hover:text-zinc-200 transition text-xs ml-1"
                      aria-label="Editar modelos"
                    >
                      ✎ Editar
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 mt-1">
                  {(caso.marca || caso.modelo) && (
                    <p className="text-sm text-zinc-400">
                      {caso.marca || ""} {caso.modelo || ""}
                    </p>
                  )}
                  {canEdit(me) && (
                    <button
                      type="button"
                      onClick={() => setEditarModelosOpen(true)}
                      className="text-zinc-500 hover:text-zinc-200 transition text-xs"
                      aria-label="Editar modelos"
                    >
                      ✎ Editar modelos
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Separador corto SOLO en la columna izquierda (linea
                decorativa). El panel de contactos abajo toma el ancho
                completo de la columna (`flex-1` del padre) para que
                nombre + chip + email + telefono + acciones puedan
                acomodarse en una sola fila. */}
            <div className="border-t border-ink-700 w-24" />
            <CasoContactosPanel caso={caso} onChanged={refreshSilencioso} />
            {/* Asesor comercial — se hereda de la empresa. El ✎ abre un modal
                aquí mismo (antes navegaba a Configuración y no había regreso). */}
            <div className="border-t border-ink-700 pt-2">
              <div className="flex items-center gap-2 flex-wrap">
                {caso.asesor_comercial ? (
                  <p className="text-xs text-zinc-400">
                    <span className="text-zinc-500">Asesor comercial:&nbsp;</span>
                    <span className="text-zinc-200 font-medium">{caso.asesor_comercial.nombre}</span>
                    {caso.asesor_comercial.email && (
                      <>{" · "}<a href={`mailto:${caso.asesor_comercial.email}`} className="text-accent-400 hover:underline">{caso.asesor_comercial.email}</a></>
                    )}
                    {caso.asesor_comercial.telefono && (
                      <>{" · "}<span>{caso.asesor_comercial.telefono}</span></>
                    )}
                  </p>
                ) : (
                  <p className="text-xs text-zinc-500">
                    <span>Asesor comercial:&nbsp;</span>
                    <span className="italic">Sin asesor asignado</span>
                  </p>
                )}
                {canEdit(me) && (
                  <button
                    type="button"
                    onClick={() => setAsesorOpen(true)}
                    className="text-zinc-500 hover:text-zinc-200 transition text-xs"
                    aria-label={caso.asesor_comercial ? "Cambiar asesor" : "Asignar asesor"}
                  >
                    {caso.asesor_comercial ? "✎ Cambiar" : "✎ Asignar"}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto lg:max-w-[460px] lg:justify-end">
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ESTADO_CASO_PILL[caso.estado]}`}>
              {caso.estado}
            </span>
            {caso.proveedor_respondio_at &&
              (canEdit(me) ? (
                <button
                  type="button"
                  onClick={() => setProveedorVistoOpen(true)}
                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition"
                >
                  ⏳ el proveedor contestó
                </button>
              ) : (
                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300">
                  ⏳ el proveedor contestó
                </span>
              ))}

            {canEdit(me) && caso.estado !== "Cerrado" && (
              <>
                <select
                  className="input text-sm py-1.5 md:text-xs md:py-1 w-full sm:w-auto"
                  value={caso.estado}
                  onChange={(e) => actualizarEstado(e.target.value as EstadoCaso)}
                >
                  {ESTADOS_CASO.filter((e) => e !== "Cerrado").map((e) => (
                    <option key={e} value={e}>{e}</option>
                  ))}
                </select>

                <select
                  className="input text-sm py-1.5 md:text-xs md:py-1 w-full sm:w-auto"
                  value={caso.asignado_a_id ?? ""}
                  onChange={(e) => actualizarAsignacion(e.target.value ? parseInt(e.target.value) : "")}
                >
                  <option value="">— sin asignar —</option>
                  {asignables.map((u) => (
                    <option key={u.id} value={u.id}>{u.nombre}</option>
                  ))}
                </select>

                {/* Hasta 6 acciones (Agregar IMEI, Notificar, Cerrar, Duplicado,
                    Revisar y Responder con el proveedor).

                    En móvil van en REJILLA de dos: apiladas a lo ancho natural
                    quedaban en escalerita, cada una de un tamaño, y se leía
                    desordenado. En escritorio siguen en fila a la derecha.

                    Una sola acción va en color —«Cerrar caso», la que termina
                    el caso—; las demás son de contorno. Antes «Notificar» iba
                    en azul fuerte junto al verde y las dos gritaban igual,
                    cuando una es rutina y la otra es el punto final. */}
                <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-wrap sm:w-auto sm:justify-end">
                  {caso.imeis.length === 0 && (
                    <button
                      onClick={() => setAgregarImeiOpen(true)}
                      className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-accent-600 hover:bg-accent-500 text-white"
                    >
                      <IconoAgregar className="h-4 w-4" /> Agregar IMEI
                    </button>
                  )}
                  <button onClick={() => setHandoffOpen(true)} className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600">
                    <IconoNotificar className="h-4 w-4" /> Notificar
                  </button>

                  <button onClick={() => setConfirmCerrar(true)} className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-emerald-600 hover:bg-emerald-500 text-white">
                    <IconoCerrar className="h-4 w-4" /> Cerrar caso
                  </button>

                  {!caso.duplicado_de_id && (
                    <button
                      onClick={() => setDuplicadoOpen(true)}
                      className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600"
                    >
                      <IconoDuplicado className="h-4 w-4" /> Duplicado
                    </button>
                  )}

                  {/* Sin IMEIs (p. ej. una solicitud de la plataforma) el ticket no se puede
                      crear desde aquí —nuestra alta lo arma con los equipos del caso; el portal
                      de Jimi sí deja abrirlo sin equipo—, pero sí se registra el número. */}
                  {caso.marca?.toLowerCase().includes("jimi") &&
                    !caso.jimi_work_order_no && (
                      <button
                        onClick={() =>
                          setJimiModal(
                            caso.imeis.length > 0
                              ? { modo: "crear" }
                              : { modo: "manual", sinEquipos: true },
                          )
                        }
                        className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600"
                      >
                        <IconoTicket className="h-4 w-4" /> Ticket en Jimi
                      </button>
                    )}

                  {/* Con ticket ya abierto, el botón de crear se esconde y en su
                      lugar aparece el de revisar: trae lo que haya contestado el
                      FAE y lo deja en la bitácora, sin entrar al portal de Jimi. */}
                  {caso.jimi_work_order_no && (
                    <button
                      onClick={() => void revisarJimi()}
                      disabled={revisandoJimi}
                      className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600 disabled:opacity-50"
                    >
                      <IconoRevisar className="h-4 w-4" /> {revisandoJimi ? "Revisando…" : "Revisar en Jimi"}
                    </button>
                  )}

                  {caso.jimi_work_order_no && (
                    <button
                      onClick={() => setResponderJimiOpen(true)}
                      className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600"
                    >
                      <IconoResponder className="h-4 w-4" /> Responder a Jimi
                    </button>
                  )}

                  {/* Queclink atiende con Zendesk: el ticket se liga solo por el
                      TE que va en el asunto («Buscar ticket»); para los que no
                      lo traen, el modal tiene «Ya tengo el número». */}
                  {caso.marca?.toLowerCase().includes("queclink") &&
                    (caso.queclink_ticket_id ? (
                      <>
                        <button
                          onClick={() => void revisarQueclink()}
                          disabled={revisandoQC}
                          className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600 disabled:opacity-50"
                        >
                          <IconoRevisar className="h-4 w-4" /> {revisandoQC ? "Revisando…" : "Revisar en Queclink"}
                        </button>
                        {/* Si Queclink CERRÓ el ticket ya no acepta respuestas: el
                            mismo botón abre la ventana en modo seguimiento (ticket
                            nuevo ligado al cerrado). Qué hace cada botón lo explica
                            la Ayuda del caso. En el celular va a
                            renglón completo: en media columna el texto no cabe y
                            se comía el ícono (medido a 412px). */}
                        <button
                          onClick={() => setResponderQCOpen(true)}
                          className="col-span-2 btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600"
                        >
                          {caso.queclink_ticket_cerrado ? (
                            <>
                              <IconoTicket className="h-4 w-4" /> Ticket de seguimiento
                            </>
                          ) : (
                            <>
                              <IconoResponder className="h-4 w-4" /> Responder a Queclink
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setCrearQCOpen(true)}
                          className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600"
                        >
                          <IconoTicket className="h-4 w-4" /> Ticket en Queclink
                        </button>
                        {caso.ticket_externo && (
                          <button
                            onClick={() => void vincularQueclink()}
                            disabled={revisandoQC}
                            className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600 disabled:opacity-50"
                          >
                            <IconoRevisar className="h-4 w-4" /> {revisandoQC ? "Buscando…" : "Buscar ticket"}
                          </button>
                        )}
                      </>
                    ))}
                </div>
              </>
            )}

            {canEdit(me) && caso.estado === "Cerrado" && (
              <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-wrap sm:w-auto sm:justify-end">
                <button
                  onClick={() => setNuevoRelacionadoOpen(true)}
                  className="btn bg-accent-600 hover:bg-accent-500 text-white text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap"
                >
                  + Caso relacionado
                </button>
                {/* Un caso cerrado también puede resultar duplicado de otro: el
                    backend lo acepta desde siempre (solo lo relaciona, conserva su
                    cierre real); el botón estaba escondido dentro del bloque de
                    «caso abierto» y por eso no se podía desde aquí. */}
                {!caso.duplicado_de_id && (
                  <button
                    onClick={() => setDuplicadoOpen(true)}
                    className="btn text-[13px] md:text-xs px-2.5 py-1.5 md:py-1 shrink-0 whitespace-nowrap inline-flex items-center justify-center gap-1.5 bg-transparent hover:bg-ink-700/60 text-zinc-200 border border-ink-600"
                  >
                    <IconoDuplicado className="h-4 w-4" /> Duplicado
                  </button>
                )}
              </div>
            )}

            <AyudaVista
              titulo="Detalle del caso"
              {...ayudaCasoDetalle({
                caso,
                puedeEditar: canEdit(me),
                hayRecorrido: idxRecorrido >= 0 && recorrido.length > 1,
              })}
            />
          </div>
        </div>

        {caso.descripcion_inicial && (
          <div className="border-t border-ink-700 pt-3">
            <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">Descripción inicial</p>
            <p className="text-sm text-zinc-300 whitespace-pre-wrap">{caso.descripcion_inicial}</p>
          </div>
        )}

        <CasoClasificacionPanel
          caso={caso}
          editable={canEdit(me) && caso.estado !== "Cerrado"}
          onUpdated={setCaso}
        />

        {caso.estado === "Cerrado" &&
          (caso.tipo_solucion_nombre ||
            caso.imeis.some((i) => i.tipo_solucion_nombre)) && (
            <div className="border-t border-ink-700 pt-3">
              <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                Solución
              </p>
              {caso.tipo_solucion_nombre ? (
                <p className="text-sm text-zinc-300">{caso.tipo_solucion_nombre}</p>
              ) : (
                <ul className="space-y-0.5">
                  {caso.imeis
                    .filter((i) => i.tipo_solucion_nombre)
                    .map((i) => (
                      <li
                        key={i.id}
                        className="text-sm text-zinc-300 flex flex-wrap items-baseline gap-x-2"
                      >
                        <span className="font-mono text-xs text-zinc-500">
                          {i.imei}
                        </span>
                        <span>{i.tipo_solucion_nombre}</span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}
      </div>

      {caso.duplicado_de_id && caso.duplicado_de_folio && (
        <div className="card bg-sky-500/10 border-sky-500/30">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm text-sky-100">
              🔗 Duplicado de{" "}
              <Link
                to={`/casos/${caso.duplicado_de_folio}`}
                className="font-mono underline decoration-dotted hover:text-sky-50"
              >
                {caso.duplicado_de_folio}
              </Link>
            </p>
            {canEdit(me) && (
              <button
                onClick={() => void quitarMarcaDuplicado()}
                className="btn bg-ink-700 hover:bg-ink-600 text-zinc-200 border border-ink-600 text-xs w-full sm:w-auto flex-shrink-0"
              >
                Quitar marca
              </button>
            )}
          </div>
        </div>
      )}

      <PanelHilo casoId={caso.id} refreshKey={hiloRefreshKey} />

      {caso.fecha_envio_proveedor && !caso.fecha_retorno_proveedor && (
        <div className="card bg-amber-500/10 border-amber-500/30">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-100">
                <span className="font-medium text-amber-300">Pendiente:</span>{" "}
                el equipo se envió al proveedor el{" "}
                <strong>
                  {new Date(caso.fecha_envio_proveedor).toLocaleDateString()}
                </strong>
                . Hace{" "}
                <strong>
                  {diasDesde(caso.fecha_envio_proveedor)}{" "}
                  día{diasDesde(caso.fecha_envio_proveedor) === 1 ? "" : "s"}
                </strong>
                . ¿Tienes respuesta del proveedor?
              </p>
              <p className="text-xs text-amber-200/70 mt-1">
                Si ya regresó el equipo, registra la fecha. Si quieres una
                fecha distinta a hoy, ajústala desde el panel "Hitos del
                caso" abajo.
              </p>
            </div>
            {canEdit(me) && (
              <button
                onClick={marcarRetornoHoy}
                className="btn bg-amber-600 hover:bg-amber-500 text-white text-xs w-full sm:w-auto flex-shrink-0"
              >
                Marcar retorno hoy
              </button>
            )}
          </div>
        </div>
      )}

      <PanelHitos caso={caso} onChanged={setCaso} />

      {/* Los equipos a la vista mientras escribes en la bitácora, sin
          brincar al tab de IMEIs (pedido de Joseph 2026-07-03). */}
      <CasoEquiposResumen imeis={caso.imeis} />

      {/* Tabs */}
      {/* Tabs: barra segmentada tipo app en mobile (5 celdas iguales con
          ícono + etiqueta corta + contador); tira con subrayado en desktop.
          Qué guarda cada pestaña lo dice la Ayuda del caso («Las pestañas»). */}
      <div className="md:hidden grid grid-cols-5 gap-1 rounded-xl bg-ink-800/40 p-1">
        {(["notas", "imeis", "evidencias", "correos", "recordatorios"] as Tab[]).map((t) => {
          const activo = tab === t;
          const count = t === "notas" ? caso.notas_count : t === "imeis" ? caso.imeis_count : null;
          return (
            <button
              key={t}
              onClick={() => setTab(t)}
              aria-label={TAB_LABEL[t]}
              aria-current={activo ? "page" : undefined}
              className={`flex flex-col items-center justify-center gap-1 rounded-lg px-0.5 py-1.5 text-[9px] leading-none transition ${
                activo ? "bg-ink-700 text-accent-300 shadow-sm" : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <span className="relative">
                {(() => {
                  const Icono = TAB_ICON[t];
                  return <Icono className="h-5 w-5" />;
                })()}
                {count != null && count > 0 && (
                  <span className="absolute -right-2.5 -top-1.5 min-w-[15px] rounded-full bg-accent-500 px-1 text-[9px] font-mono leading-[15px] text-ink-950">
                    {count}
                  </span>
                )}
              </span>
              <span className="max-w-full truncate">{TAB_CORTO[t]}</span>
            </button>
          );
        })}
      </div>

      <div className="hidden md:flex border-b border-ink-700 gap-1">
        {(["notas", "imeis", "evidencias", "correos", "recordatorios"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px transition whitespace-nowrap ${
              tab === t ? "border-accent-500 text-accent-400" : "border-transparent text-zinc-400 hover:text-white"
            }`}
          >
            {TAB_LABEL[t]}
            {t === "notas" && <span className="ml-1 text-xs text-zinc-500">({caso.notas_count})</span>}
            {t === "imeis" && <span className="ml-1 text-xs text-zinc-500">({caso.imeis_count})</span>}
          </button>
        ))}
      </div>

      {/* Reserva de alto: al cambiar de caso el contenido de la pestaña llega
          después (su propio fetch) y el documento se encogía de 2618 a 1773 px
          para volver a crecer 130 ms más tarde — el piso se movía debajo del
          dedo. Con un mínimo estable ya no se encoge. */}
      <div className="min-h-[70vh]">
        {tab === "notas" && (
          <TabNotas
            caso={caso}
            onChanged={refreshSilencioso}
            onVerCorreos={(id) => {
              setCorreoAAbrir(id ?? null);
              setTab("correos");
            }}
          />
        )}
        {tab === "imeis" && (
          <TabImeis
            caso={caso}
            onChanged={refreshSilencioso}
            onAgregar={() => setAgregarImeiOpen(true)}
          />
        )}
        {tab === "evidencias" && <TabEvidencias caso={caso} onChanged={refreshSilencioso} />}
        {tab === "correos" && (
          <TabCorreos
            caso={caso}
            onChanged={refreshSilencioso}
            abrirCorreoId={correoAAbrir}
          />
        )}
        {tab === "recordatorios" && (
          <TabRecordatorios caso={caso} asignables={asignables} />
        )}
      </div>

      <CerrarCasoModal
        caso={caso}
        open={confirmCerrar}
        onOpenChange={setConfirmCerrar}
        onClosed={setCaso}
      />

      <ProveedorVistoModal
        open={proveedorVistoOpen}
        onOpenChange={setProveedorVistoOpen}
        casoId={caso.id}
        onListo={setCaso}
      />

      <NuevoCasoModal
        open={nuevoRelacionadoOpen}
        onClose={() => setNuevoRelacionadoOpen(false)}
        prefill={relacionadoPrefill}
        onCreated={(nuevo) => {
          // No navegamos automatico; refrescamos el hilo para que aparezca
          // el nuevo caso en el panel y el usuario decide si entra.
          setHiloRefreshKey((k) => k + 1);
          toast.success("Caso relacionado creado", nuevo.folio);
        }}
      />

      <HandoffModal
        open={handoffOpen}
        onOpenChange={setHandoffOpen}
        casoId={caso.id}
        casoFolio={caso.folio}
        candidatos={asignables}
        onDone={refreshSilencioso}
      />

      <AgregarImeiModal
        open={agregarImeiOpen}
        onOpenChange={setAgregarImeiOpen}
        casoId={caso.id}
        casoModelo={caso.modelo}
        onAdded={refreshSilencioso}
      />

      <EditarModelosModal
        open={editarModelosOpen}
        onClose={() => setEditarModelosOpen(false)}
        caso={caso}
        onSaved={setCaso}
      />

      {jimiModal && (
        <AbrirTicketJimiModal
          casoId={caso.id}
          modoInicial={jimiModal.modo}
          numeroInicial={jimiModal.numero}
          sinEquipos={jimiModal.sinEquipos}
          onClose={() => setJimiModal(null)}
          onDone={() => {
            setJimiModal(null);
            void load({ silent: true });
          }}
        />
      )}
      {duplicadoOpen && (
        <MarcarDuplicadoModal
          caso={caso}
          onClose={() => setDuplicadoOpen(false)}
          onDone={(actualizado) => {
            setCaso(actualizado);
            setDuplicadoOpen(false);
          }}
        />
      )}
      {responderJimiOpen && caso.jimi_work_order_no && (
        <ResponderProveedorModal
          casoId={caso.id}
          proveedor="Jimi"
          ticket={caso.jimi_work_order_no}
          rutaEnviar={`/api/casos/${caso.id}/jimi/responder`}
          rutaTraducir={`/api/casos/${caso.id}/jimi/traducir`}
          onClose={() => setResponderJimiOpen(false)}
          onEnviado={refreshSilencioso}
        />
      )}
      {crearQCOpen && (
        <CrearTicketQueclinkModal
          casoId={caso.id}
          onClose={() => setCrearQCOpen(false)}
          onCreado={refreshSilencioso}
        />
      )}
      {responderQCOpen && caso.queclink_ticket_id && (
        <ResponderProveedorModal
          casoId={caso.id}
          proveedor="Queclink"
          ticket={`#${caso.queclink_ticket_id}`}
          rutaEnviar={`/api/casos/${caso.id}/queclink/responder`}
          rutaSeguimiento={`/api/casos/${caso.id}/queclink/seguimiento`}
          cerrado={caso.queclink_ticket_cerrado}
          onTicketCerrado={refreshSilencioso}
          onClose={() => setResponderQCOpen(false)}
          onEnviado={refreshSilencioso}
        />
      )}
      {asesorOpen && (
        <AsignarAsesorModal
          clienteId={caso.cliente_id}
          clienteNombre={caso.cliente_nombre}
          asesorActualId={caso.asesor_comercial?.id ?? null}
          onClose={() => setAsesorOpen(false)}
          onSaved={refreshSilencioso}
        />
      )}
    </div>
    </>
  );
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function diasDesde(iso: string): number {
  const start = new Date(iso + "T00:00:00");
  const now = new Date();
  const diff = now.getTime() - start.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}
